import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { TrendingUp, RefreshCw, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/errorUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { currentFY, fyDropdownOptions } from '../lib/fyUtils';
import { EmployeeSelect } from '../components/EmployeeAutocomplete';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TDSProjection = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [fy, setFy] = useState(currentFY());
  const [projection, setProjection] = useState(null);
  const [worksheet, setWorksheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('projection');
  const [bulkBusy, setBulkBusy] = useState(false);

  // Streams a CSV containing per-employee TDS projection (or worksheet if requested
  // from the worksheet tab) for every active employee in the chosen company. Useful
  // for sharing with auditors / Form 24Q teams without round-tripping each employee.
  const downloadBulkCSV = async (kind /* 'projection' | 'worksheet' */) => {
    setBulkBusy(true);
    try {
      const path = kind === 'worksheet' ? 'annual-worksheet/bulk-csv' : 'projection/bulk-csv';
      const url = `${API_URL}/statutory/tds/${path}?company_id=${selectedCompany}&financial_year=${fy}`;
      const response = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `${kind === 'worksheet' ? 'Annual_Tax_Worksheet' : 'TDS_Projection'}_Bulk_${fy}.csv`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${kind === 'worksheet' ? 'Annual worksheet' : 'TDS projection'} CSV downloaded`);
    } catch (err) {
      const msg = err.response?.data instanceof Blob
        ? 'Bulk export failed — check that employees and CTCs are configured for this company.'
        : formatApiError(err);
      toast.error(msg);
    } finally {
      setBulkBusy(false);
    }
  };

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data); setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/employees?company_id=${selectedCompany}`, { withCredentials: true });
      setEmployees(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  useEffect(() => { if (selectedCompany) fetchEmployees(); }, [selectedCompany, fetchEmployees]);



  const computeProjection = async () => {
    if (!selectedEmployee) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/statutory/tds/project`, { employee_id: selectedEmployee, company_id: selectedCompany, financial_year: fy }, { withCredentials: true });
      setProjection(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  const fetchWorksheet = async () => {
    if (!selectedEmployee) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/statutory/tds/annual-worksheet?employee_id=${selectedEmployee}&financial_year=${fy}&company_id=${selectedCompany}`, { withCredentials: true });
      setWorksheet(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  return (
    <MainLayout>
      <div data-testid="tds-projection-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">TDS Projection</h1>
            <p className="text-sm text-slate-500 mt-1">Monthly TDS projection, annual tax worksheet, and regime comparison</p>
          </div>
          <div className="flex items-center space-x-3">
            <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="w-72">
              <EmployeeSelect
                companyId={selectedCompany}
                value={selectedEmployee}
                onChange={(empId) => setSelectedEmployee(empId)}
                placeholder="Search Employee by Code or Name…"
                data-testid="employee-selector"
              />
            </div>
            <select value={fy} onChange={(e) => setFy(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="fy-selector">
              {fyDropdownOptions(2, 2).map(label => (
                <option key={label} value={label}>{`FY ${label}`}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Bulk export — works company-wide, no individual employee needed.
            Lets HR generate a CSV of TDS projection or annual worksheet for the entire
            workforce in one shot for sharing with auditors / Form 24Q teams. */}
        <div data-testid="tds-bulk-bar" className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800">Bulk export — all employees</p>
            <p className="text-xs text-slate-600">Generate a CSV for every active employee in this company for FY {fy}. No individual selection required.</p>
          </div>
          <button data-testid="bulk-projection-csv-btn" onClick={() => downloadBulkCSV('projection')} disabled={bulkBusy || !selectedCompany}
            title="Per-employee monthly TDS projection (regime + projected income + remaining tax + monthly TDS)"
            className="flex items-center gap-2 bg-white border border-blue-300 text-blue-800 px-3 py-2 rounded-lg text-xs font-medium hover:bg-blue-100 disabled:opacity-50">
            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Bulk TDS Projection CSV
          </button>
          <button data-testid="bulk-worksheet-csv-btn" onClick={() => downloadBulkCSV('worksheet')} disabled={bulkBusy || !selectedCompany}
            title="OLD vs NEW regime side-by-side, recommendation, savings — 1 row per employee"
            className="flex items-center gap-2 bg-[#0052FF] text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-[#0040CC] disabled:opacity-50">
            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Bulk Annual Worksheet CSV
          </button>
        </div>

        {selectedEmployee && (
          <>
            <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
              {['projection', 'worksheet'].map((tab) => (
                <button key={tab} data-testid={`tab-${tab}`} onClick={() => { setActiveTab(tab); if (tab === 'projection') computeProjection(); else fetchWorksheet(); }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === tab ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                  {tab === 'projection' ? 'Monthly Projection' : 'Annual Worksheet'}
                </button>
              ))}
            </div>

            {activeTab === 'projection' && (
              <div className="space-y-4">
                <div className="flex justify-end"><button data-testid="compute-projection-btn" onClick={computeProjection} disabled={loading} className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC] disabled:opacity-50"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /><span>Compute</span></button></div>
                {projection?.warning && (
                  <div data-testid="projection-warning" className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                    {projection.warning}
                  </div>
                )}
                {projection && (
                  <>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-500">Projected Annual Income</p><p className="text-xl font-bold text-slate-900">{projection.projected_annual_income?.toLocaleString()}</p></div>
                      <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-500">Taxable Income</p><p className="text-xl font-bold text-amber-600">{projection.taxable_income?.toLocaleString()}</p></div>
                      <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-500">Total Tax</p><p className="text-xl font-bold text-red-600">{projection.total_tax?.toLocaleString()}</p></div>
                      <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-500">Monthly TDS</p><p className="text-xl font-bold text-[#0052FF]">{projection.adjusted_monthly_tds?.toLocaleString()}</p></div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <h3 className="text-sm font-semibold text-slate-800 mb-3">Projection Details</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="space-y-2">
                          <div className="flex justify-between"><span className="text-slate-500">Regime</span><span className="font-medium uppercase">{projection.regime}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Income Till Date</span><span className="font-mono">{projection.income_till_date?.toLocaleString()}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">TDS Deducted Till Date</span><span className="font-mono">{projection.tds_till_date?.toLocaleString()}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Months Remaining</span><span className="font-mono">{projection.months_remaining}</span></div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between"><span className="text-slate-500">Tax on Income</span><span className="font-mono">{projection.tax_on_income?.toLocaleString()}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Surcharge</span><span className="font-mono">{projection.surcharge?.toLocaleString()}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Cess (4%)</span><span className="font-mono">{projection.cess?.toLocaleString()}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Effective Rate</span><span className="font-mono text-red-600">{projection.effective_rate}%</span></div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'worksheet' && (
              <div className="space-y-4">
                <div className="flex justify-end"><button data-testid="fetch-worksheet-btn" onClick={fetchWorksheet} disabled={loading} className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC] disabled:opacity-50"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /><span>Compute</span></button></div>
                {worksheet?.warning && (
                  <div data-testid="worksheet-warning" className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                    {worksheet.warning}
                  </div>
                )}
                {worksheet && (
                  <>
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <h3 className="text-sm font-semibold text-slate-800 mb-3">Regime Comparison</h3>
                      <div className="grid grid-cols-2 gap-6">
                        {['old_regime', 'new_regime'].map((key) => {
                          const r = worksheet[key];
                          const isSelected = (key === 'old_regime' && worksheet.selected_regime === 'old') || (key === 'new_regime' && worksheet.selected_regime === 'new');
                          const isRecommended = worksheet.recommendation === key.split('_')[0];
                          return (
                            <div key={key} className={`border rounded-xl p-4 ${isSelected ? 'border-[#0052FF] bg-blue-50/30' : 'border-slate-200'}`}>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold">{key === 'old_regime' ? 'Old Regime' : 'New Regime'}</h4>
                                <div className="flex items-center space-x-2">
                                  {isSelected && <span className="text-xs bg-[#0052FF] text-white px-2 py-0.5 rounded">Selected</span>}
                                  {isRecommended && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">Recommended</span>}
                                </div>
                              </div>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-slate-500">Taxable Income</span><span className="font-mono">{r?.taxable_income?.toLocaleString()}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Tax</span><span className="font-mono">{r?.tax_on_income?.toLocaleString()}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Surcharge</span><span className="font-mono">{r?.surcharge?.toLocaleString()}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Cess</span><span className="font-mono">{r?.cess?.toLocaleString()}</span></div>
                                <div className="flex justify-between border-t border-slate-200 pt-2"><span className="font-semibold">Total Tax</span><span className="font-mono font-bold text-red-600">{r?.total_tax?.toLocaleString()}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Monthly TDS</span><span className="font-mono">{r?.monthly_tds?.toLocaleString()}</span></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {worksheet.savings > 0 && <p className="text-sm text-emerald-600 mt-3 font-medium text-center">Savings with {worksheet.recommendation} regime: Rs. {worksheet.savings?.toLocaleString()}</p>}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {!selectedEmployee && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-base font-semibold text-slate-600">Select an Employee</h3>
            <p className="text-sm text-slate-400 mt-1">Choose an employee to view TDS projections and worksheets</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default TDSProjection;
